// Pure Web Audio API — no Tone.js dependency.
// Frequencies 1-12 mapped to chromatic scale C4-B4 in Hz.

const FREQ_HZ: Record<number, number> = {
  1: 261.63,  // C4
  2: 277.18,  // C#4
  3: 293.66,  // D4
  4: 311.13,  // D#4
  5: 329.63,  // E4
  6: 349.23,  // F4
  7: 369.99,  // F#4
  8: 392.00,  // G4
  9: 415.30,  // G#4
  10: 440.00, // A4
  11: 466.16, // A#4
  12: 493.88, // B4
};

class AudioEngine {
  private ctx: AudioContext | null = null;
  private initialized = false;
  private _muted = false;
  private masterVolume = 0.7;
  private sfxVolume = 0.8;
  private ambientVolume = 0.3;

  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;

  private activeNodes = 0;
  private readonly MAX_ACTIVE_NODES = 24;

  async init(): Promise<void> {
    if (this.initialized) return;

    try {
      this.ctx = new AudioContext();

      // Master → destination
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.masterVolume;
      this.masterGain.connect(this.ctx.destination);

      // SFX channel
      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.sfxVolume;
      this.sfxGain.connect(this.masterGain);

      // Ambient channel
      this.ambientGain = this.ctx.createGain();
      this.ambientGain.gain.value = this.ambientVolume;
      this.ambientGain.connect(this.masterGain);

      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }

      this.initialized = true;
    } catch {
      console.warn('AudioEngine: Web Audio API not available');
    }
  }

  /** Bell-like strike tone at the given game frequency (1-12). */
  playStrike(frequency: number): void {
    if (!this.canPlay()) return;
    if (this.activeNodes >= this.MAX_ACTIVE_NODES) return;
    const hz = FREQ_HZ[frequency];
    if (!hz) return;

    const ctx = this.ctx!;  // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const now = ctx.currentTime;

    // Sine oscillator with fast attack, medium decay → bell character
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = hz;

    // Harmonic overtone for brightness
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = hz * 2.01; // slight detune for shimmer

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(0.25, now + 0.005);
    env.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

    const env2 = ctx.createGain();
    env2.gain.setValueAtTime(0, now);
    env2.gain.linearRampToValueAtTime(0.08, now + 0.005);
    env2.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

    osc.connect(env).connect(this.sfxGain!);  // eslint-disable-line @typescript-eslint/no-non-null-assertion
    osc2.connect(env2).connect(this.sfxGain!);  // eslint-disable-line @typescript-eslint/no-non-null-assertion

    this.activeNodes += 2;
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
      this.activeNodes--;
    };
    osc2.onended = () => {
      osc2.disconnect();
      env2.disconnect();
      this.activeNodes--;
    };

    osc.start(now);
    osc.stop(now + 0.7);
    osc2.start(now);
    osc2.stop(now + 0.5);
  }

  /** Short tone at the new frequency, delayed to match ring propagation timing. */
  playTransmutation(_oldFreq: number, newFreq: number, delayMs: number): void {
    if (!this.canPlay()) return;
    if (this.activeNodes >= this.MAX_ACTIVE_NODES) return;
    const hz = FREQ_HZ[newFreq];
    if (!hz) return;

    const ctx = this.ctx!;  // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const start = ctx.currentTime + delayMs / 1000;

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = hz;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(0.12, start + 0.02);
    env.gain.exponentialRampToValueAtTime(0.001, start + 0.35);

    osc.connect(env).connect(this.sfxGain!);  // eslint-disable-line @typescript-eslint/no-non-null-assertion

    this.activeNodes++;
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
      this.activeNodes--;
    };

    osc.start(start);
    osc.stop(start + 0.4);
  }

  /** Sustained chord from all node frequencies — plays on resonance achieved. */
  playResonance(frequencies: number[]): void {
    if (!this.canPlay()) return;

    const ctx = this.ctx!;  // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const now = ctx.currentTime;
    const uniqueHz = [...new Set(frequencies.map((f) => FREQ_HZ[f]).filter(Boolean))] as number[];
    if (uniqueHz.length === 0) return;

    for (const hz of uniqueHz) {
      if (this.activeNodes >= this.MAX_ACTIVE_NODES) return;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = hz;

      const env = ctx.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(0.06, now + 0.8);
      env.gain.setValueAtTime(0.06, now + 1.5);
      env.gain.exponentialRampToValueAtTime(0.001, now + 3.0);

      osc.connect(env).connect(this.ambientGain!);  // eslint-disable-line @typescript-eslint/no-non-null-assertion

      this.activeNodes++;
      osc.onended = () => {
        osc.disconnect();
        env.disconnect();
        this.activeNodes--;
      };

      osc.start(now);
      osc.stop(now + 3.2);
    }
  }

  setMasterVolume(value: number): void {
    this.masterVolume = clamp01(value);
    if (this.masterGain) this.masterGain.gain.value = this.masterVolume;
  }

  setSfxVolume(value: number): void {
    this.sfxVolume = clamp01(value);
    if (this.sfxGain) this.sfxGain.gain.value = this.sfxVolume;
  }

  setAmbientVolume(value: number): void {
    this.ambientVolume = clamp01(value);
    if (this.ambientGain) this.ambientGain.gain.value = this.ambientVolume;
  }

  mute(): void {
    this._muted = true;
    if (this.masterGain) this.masterGain.gain.value = 0;
  }

  unmute(): void {
    this._muted = false;
    if (this.masterGain) this.masterGain.gain.value = this.masterVolume;
  }

  toggleMute(): void {
    if (this._muted) this.unmute();
    else this.mute();
  }

  isMuted(): boolean {
    return this._muted;
  }

  isReady(): boolean {
    return this.initialized;
  }

  private canPlay(): boolean {
    return this.initialized && !this._muted && this.ctx !== null;
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

// Singleton
export const audioEngine = new AudioEngine();
